import React, { useEffect, useRef } from 'react'
import { X, HelpCircle, BookOpen } from 'lucide-react'
import HelpBlockRenderer from '../../../components/help/HelpBlockRenderer'

const P = { pri: '#0a5169', priM: '#e8f2f6' }

function HelpSection({ section, highlighted, sectionIdPrefix }) {
  const hasBlocks = Array.isArray(section.blocks) && section.blocks.length > 0
  return (
    <section
      id={`${sectionIdPrefix}-${section.id}`}
      className={`rounded-lg border p-4 scroll-mt-4 transition-colors ${
        highlighted
          ? 'border-teal-300 bg-teal-50/80 shadow-sm'
          : 'border-slate-200 bg-white'
      }`}
    >
      <h4 className="text-sm font-bold text-slate-900 mb-2">{section.title}</h4>
      {hasBlocks ? (
        <HelpBlockRenderer blocks={section.blocks} />
      ) : (
        <div className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">
          {(section.body || '').split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return (
                <strong key={i} className="font-semibold text-slate-800">
                  {part.slice(2, -2)}
                </strong>
              )
            }
            return <span key={i}>{part}</span>
          })}
        </div>
      )}
    </section>
  )
}

/**
 * Right-side slide-over help panel (shared by investigation case + pipeline).
 */
export default function SlideOverHelpPanel({
  open,
  onClose,
  title,
  subtitle,
  sections = [],
  focusSectionId = 'overview',
  sectionIdPrefix = 'context-help',
  footer,
  intro,
}) {
  const scrollRef = useRef(null)
  const titleId = `${sectionIdPrefix}-title`

  useEffect(() => {
    if (!open) return
    const t = window.setTimeout(() => {
      const el = document.getElementById(`${sectionIdPrefix}-${focusSectionId}`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 120)
    return () => window.clearTimeout(t)
  }, [open, focusSectionId, sectionIdPrefix])

  useEffect(() => {
    if (!open) return
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <>
      <div
        className="fixed inset-0 bg-black/30 z-[60] transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-xl z-[70] flex flex-col border-l border-slate-200"
        style={{ boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0 bg-slate-50">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full shrink-0"
              style={{ background: P.priM, color: P.pri }}
            >
              <HelpCircle size={18} />
            </div>
            <div className="min-w-0">
              <h2 id={titleId} className="text-sm font-bold text-slate-900 truncate">
                {title}
              </h2>
              {subtitle ? (
                <p className="text-[11px] text-slate-500 truncate">{subtitle}</p>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-slate-200 text-slate-500 shrink-0"
            aria-label="Close help"
          >
            <X size={18} />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
          {intro ?? (
            <p className="text-xs text-slate-500 leading-relaxed px-1">
              The highlighted section matches your current context. Press{' '}
              <kbd className="px-1 py-0.5 rounded bg-slate-100 text-[10px] font-mono">Esc</kbd> to close.
            </p>
          )}
          {sections.map((section) => (
            <HelpSection
              key={section.id}
              section={section}
              highlighted={section.id === focusSectionId}
              sectionIdPrefix={sectionIdPrefix}
            />
          ))}
        </div>

        {footer ? (
          <div className="shrink-0 border-t border-slate-200 px-4 py-3 bg-slate-50">{footer}</div>
        ) : null}
      </aside>
    </>
  )
}

export function HelpFooterNote({ children }) {
  return (
    <div className="flex items-start gap-2 text-xs text-slate-600">
      <BookOpen size={14} className="shrink-0 mt-0.5 text-slate-400" />
      <p>{children}</p>
    </div>
  )
}
